import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PenLine, ShieldCheck } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/** Public /sign/:token — no auth. */
export default function SignaturePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signerName, setSignerName] = useState("");
  const [typed, setTyped] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/sign/${token}`);
      setData(data);
      setSignerName(data?.request?.signer_name || "");
      if (data?.request?.status === "signed") setSigned(true);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Link invalid or expired" });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    if (!typed.trim() || !signerName.trim()) { toast.error("Type your name and signature"); return; }
    if (!acceptTerms) { toast.error("Please accept the terms"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/sign/${token}`, {
        typed_signature: typed.trim(),
        signer_name: signerName.trim(),
        signer_email: data?.request?.signer_email,
        accept_terms: true,
      });
      setSigned(true);
      toast.success("Signed. Thank you.");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to sign")); }
    finally { setSubmitting(false); }
  };

  const quickApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/sign/${token}/quick-approve`);
      setSigned(true);
      toast.success("Approved.");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (data?.error) return <div className="p-10 text-sm text-rose-600">{data.error}</div>;
  if (!data) return null;

  const { request, doc, line_items } = data;
  const totalLines = (line_items || []).length;

  return (
    <div className="min-h-screen bg-muted/10 p-6" data-testid="signature-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="text-xs uppercase text-muted-foreground tracking-wide">
          Approval requested · {request.doc_type.toUpperCase()} · {doc?.number}
        </div>
        <h1 className="text-3xl font-bold">{doc?.title}</h1>

        {doc?.body_html && (
          <div className="border border-border bg-background p-4 text-sm prose max-w-none" dangerouslySetInnerHTML={{ __html: doc.body_html }} />
        )}

        {totalLines > 0 && (
          <div className="border border-border bg-background">
            <div className="px-4 py-2 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-wide">Line items ({totalLines})</div>
            <table className="w-full text-xs">
              <thead className="bg-muted/20 border-b border-border">
                <tr className="text-left text-[10px] uppercase text-muted-foreground">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2 w-16">Qty</th>
                  <th className="px-3 py-2 w-16">Unit</th>
                  <th className="px-3 py-2 w-24">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {line_items.map((li) => (
                  <tr key={li.id}>
                    <td className="px-3 py-2 font-medium">{li.name}</td>
                    <td className="px-3 py-2">{li.qty}</td>
                    <td className="px-3 py-2">{li.unit}</td>
                    <td className="px-3 py-2 font-mono">₹{Number(li.rate || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {signed || request.status === "signed" ? (
          <div className="border border-emerald-300 bg-emerald-50 text-emerald-900 p-6 text-center space-y-2" data-testid="signature-thankyou">
            <ShieldCheck className="w-8 h-8 mx-auto text-emerald-600" />
            <div className="font-semibold text-lg">Signed. Thank you.</div>
            <div className="text-xs">A copy has been shared with the sender.</div>
          </div>
        ) : (
          <div className="border border-border bg-background p-4 space-y-4" data-testid="signature-form">
            <div className="text-sm font-semibold flex items-center gap-2">
              <PenLine className="w-4 h-4" /> Sign this document
            </div>

            <button type="button" onClick={quickApprove} disabled={submitting} className="w-full text-sm px-3 py-2 border border-primary/40 text-primary hover:bg-primary/5 disabled:opacity-50 font-medium" data-testid="signature-quick-approve">
              I approve (quick one-click)
            </button>
            <div className="text-[10px] text-muted-foreground text-center uppercase tracking-wide">or type your signature below</div>

            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Full name</label>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="signature-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Typed signature</label>
              <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your name in a script font" className="w-full text-lg px-2 py-2 mt-0.5 bg-background border border-border italic" style={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive' }} data-testid="signature-typed" />
            </div>
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5" data-testid="signature-accept" />
              <span>By signing I agree to the terms and confirm my identity. My IP and timestamp will be recorded.</span>
            </label>
            <button type="button" onClick={sign} disabled={submitting || !typed.trim() || !acceptTerms} className="w-full text-sm px-3 py-2 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium" data-testid="signature-submit">
              {submitting ? "Signing…" : "Sign & Approve"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
